/** Read-only display pieces: empty state, KPI tile, money/date, timeline, approval banner. */
import { ArrowDownRight, ArrowUpRight, Clock, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ago, dateTime, peso, pesoCompact, shortDate, when } from '../../lib/format';
import type { ApprovalState } from '../../lib/types';
import { ApprovalBadge } from './badges';
import { Button, cx } from './core';

/** An empty screen is an invitation to act: say what goes here and the next step. */
export function EmptyState({ icon, title, body, action, secondary, compact, className }: { icon?: ReactNode; title: string; body?: ReactNode; action?: ReactNode; secondary?: ReactNode; compact?: boolean; className?: string }) {
  return (
    <div className={cx('flex flex-col items-center text-center', compact ? 'gap-1.5 px-4 py-8' : 'gap-2 px-6 py-14', className)}>
      {icon && <div className="mb-2 grid h-12 w-12 place-items-center rounded-full border border-dashed border-strong text-muted [&>svg]:h-5 [&>svg]:w-5">{icon}</div>}
      <p className="text-base font-semibold">{title}</p>
      {body && <div className="max-w-md text-sm text-muted">{body}</div>}
      {(action || secondary) && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}

/** Money with tabular figures; negatives in danger colour when `signed`. */
export function Money({ value, whole, compact, signed, className }: { value: string | number | null | undefined; whole?: boolean; compact?: boolean; signed?: boolean; className?: string }) {
  const text = compact ? pesoCompact(value) : peso(value, whole);
  return <span className={cx('money', signed && text.startsWith('-') && 'text-danger', className)}>{text}</span>;
}

/** A date in Asia/Manila with the full timestamp on hover. */
export function DateText({ value, mode = 'date', className }: { value: string | Date | null | undefined; mode?: 'date' | 'datetime' | 'when' | 'ago'; className?: string }) {
  if (!value) return <span className={className}>—</span>;
  const text = mode === 'date' ? shortDate(value) : mode === 'datetime' ? dateTime(value) : mode === 'when' ? when(value) : ago(value);
  const iso = typeof value === 'string' ? value : value.toISOString();
  return (
    <time dateTime={iso} title={`${dateTime(value)} (Manila)`} className={cx('tabular whitespace-nowrap', className)}>
      {text}
    </time>
  );
}

/**
 * KPI tile (FR-DSH-01). The number is the hero; the label names it; the delta says
 * compared with what. The whole tile is a link to the filtered list behind it.
 */
export function StatCard({ label, value, sub, delta, deltaLabel, href, icon, loading, tone }: { label: string; value: ReactNode; sub?: ReactNode; delta?: number | null; deltaLabel?: string; href?: string; icon?: ReactNode; loading?: boolean; tone?: 'default' | 'attention' }) {
  const up = (delta ?? 0) >= 0;
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted">{label}</p>
        {icon && <span className="text-muted [&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
      </div>
      {loading ? (
        <span className="mt-2 block h-8 w-28 animate-pulse rounded bg-sunken" />
      ) : (
        <p className="display tabular mt-1 text-[28px] leading-9">{value}</p>
      )}
      <div className="mt-1 flex min-h-5 flex-wrap items-center gap-x-2 text-xs text-muted">
        {delta !== undefined && delta !== null && (
          <span className={cx('inline-flex items-center gap-0.5 font-semibold', up ? 'text-brand' : 'text-danger')}>
            {up ? <ArrowUpRight aria-hidden className="h-3.5 w-3.5" /> : <ArrowDownRight aria-hidden className="h-3.5 w-3.5" />}
            {Math.abs(delta).toFixed(1)}%<span className="sr-only">{up ? ' up' : ' down'}</span>
          </span>
        )}
        {deltaLabel && <span>{deltaLabel}</span>}
        {sub && <span>{sub}</span>}
      </div>
    </>
  );
  const cls = cx(
    'block rounded-panel border bg-card p-4 shadow-panel transition-colors',
    tone === 'attention' ? 'border-warn/50' : 'border-line',
    href && 'hover:border-strong focus-visible:border-brand',
  );
  return href ? (
    <Link to={href} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export interface TimelineEvent {
  id: string;
  at: string;
  title: ReactNode;
  who?: string;
  detail?: ReactNode;
  tone?: 'default' | 'good' | 'bad' | 'pending';
}

/** Item history / transfer and audit progress (FR-INV-07). Newest first, read-only. */
export function StatusTimeline({ events, empty = 'No history yet.' }: { events: TimelineEvent[]; empty?: string }) {
  if (!events.length) return <p className="py-4 text-sm text-muted">{empty}</p>;
  return (
    <ol className="relative ml-2 border-l border-line">
      {events.map((e) => (
        <li key={e.id} className="relative pb-5 pl-5 last:pb-0">
          <span
            aria-hidden
            className={cx(
              'absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-card',
              e.tone === 'good' ? 'bg-ok' : e.tone === 'bad' ? 'bg-danger' : e.tone === 'pending' ? 'border-2 border-warn bg-card' : 'bg-strong',
            )}
          />
          <p className="text-sm font-semibold">{e.title}</p>
          <p className="text-xs text-muted">
            <DateText value={e.at} mode="datetime" />
            {e.who && <> by {e.who}</>}
          </p>
          {e.detail && <div className="mt-1 text-sm text-muted">{e.detail}</div>}
        </li>
      ))}
    </ol>
  );
}

/**
 * Shown on any record waiting for (or decided by) a second person (FR-ACC-04). The
 * requester never sees approve buttons for their own request — pass canDecide=false.
 */
export function ApprovalBanner({ state, kind, requester, requestedAt, approver, decidedAt, reason, amount, canDecide, onApprove, onReject, busy }: {
  state: ApprovalState;
  kind: string;
  requester: string;
  requestedAt: string;
  approver?: string | null;
  decidedAt?: string | null;
  reason?: string | null;
  amount?: string | null;
  canDecide?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  busy?: boolean;
}) {
  const pending = state === 'PENDING';
  return (
    <div className={cx('flex flex-col gap-3 rounded-panel border p-4 sm:flex-row sm:items-center', pending ? 'border-warn/50 bg-warn-soft/60' : 'border-line bg-sunken')}>
      <span className={cx('grid h-10 w-10 shrink-0 place-items-center rounded-full', pending ? 'bg-warn-soft text-warn' : 'bg-card text-muted')}>
        {pending ? <Clock aria-hidden className="h-5 w-5" /> : <ShieldCheck aria-hidden className="h-5 w-5" />}
      </span>
      <div className="min-w-0 flex-1 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{kind}</span>
          <ApprovalBadge value={state} />
          {amount && <Money value={amount} className="font-semibold" />}
        </div>
        <p className="mt-0.5 text-muted">
          Requested by {requester}, <DateText value={requestedAt} mode="when" />
          {approver && decidedAt && (
            <>
              {' '}
              · {state === 'APPROVED' ? 'approved' : 'decided'} by {approver}, <DateText value={decidedAt} mode="when" />
            </>
          )}
        </p>
        {reason && <p className="mt-1">“{reason}”</p>}
      </div>
      {pending && canDecide && (
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onReject} disabled={busy}>
            Reject
          </Button>
          <Button onClick={onApprove} loading={busy}>
            Approve
          </Button>
        </div>
      )}
    </div>
  );
}
